import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight, ShieldCheck, TrendingUp, Compass, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { SITE, IMAGES, WHY, TESTIMONIALS } from "@/data/site";
import { MaskedLines, Reveal, Overline, easeQuart } from "@/components/Motion";
import { Star, Quote } from "lucide-react";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATS = [
  { value: "8+", label: "Years of practice" },
  { value: "3", label: "Core planning services" },
  { value: "100%", label: "Independent & unbiased" },
  { value: "1:1", label: "Personalised guidance" },
];

const VALUES = ["Financial Planning", "•", "Retirement", "•", "Wealth Creation", "•", "Goal Mapping", "•", "Investment Strategy", "•", "Tax Efficiency", "•"];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={IMAGES.hero} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-brand-navy/60" />
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-navy via-brand-navy/70 to-brand-navy/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-transparent to-transparent" />
      </motion.div>

      <motion.div style={{ y: overlayY }} className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-10 pb-20 pt-40">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          <Overline className="text-brand-sky">Arivu Finserve — Financial Planning, India</Overline>
        </motion.div>

        <h1 className="font-serif text-white font-light leading-[0.95] mt-6 text-5xl sm:text-6xl lg:text-8xl">
          <MaskedLines lines={["Grow your wealth", "with a plan that"]} delay={0.35} />
          <span className="reveal-line">
            <motion.span
              className="block italic text-brand-accent"
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 1, ease: easeQuart, delay: 0.35 + 2 * 0.12 }}
            >
              actually fits you.
            </motion.span>
          </span>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-8 flex flex-col sm:flex-row sm:items-center gap-6 max-w-3xl"
        >
          <p className="text-white/70 text-lg font-light leading-relaxed">
            Independent, goal-based financial planning from {SITE.advisor} — turning your income, goals and dreams into a clear, disciplined roadmap.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href={SITE.calendly}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-book-btn"
            className="inline-flex items-center gap-2 rounded-full bg-brand-accent text-white text-sm font-medium px-8 py-4 hover:bg-white hover:text-brand-navy transition-colors duration-300 group"
          >
            Book a free consultation
            <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
          <Link
            to="/services"
            data-testid="hero-services-btn"
            className="inline-flex items-center gap-2 rounded-full border border-white/30 text-white text-sm font-medium px-8 py-4 hover:bg-white/10 transition-colors duration-300"
          >
            Explore services <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </motion.div>
    </section>
  );
}

function StatBar() {
  return (
    <section className="bg-brand-navy text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10 border-x border-white/10">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.08} className="py-10 px-6 lg:px-10">
            <div className="font-serif text-4xl lg:text-5xl text-brand-accent">{s.value}</div>
            <div className="text-white/50 text-sm mt-2 uppercase tracking-wider">{s.label}</div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function EditorialMarquee() {
  return (
    <div className="py-8 bg-brand-bg border-y border-brand-border overflow-hidden">
      <Marquee speed={40} gradient={false}>
        {VALUES.concat(VALUES).map((v, i) => (
          <span
            key={i}
            className={`font-serif text-5xl lg:text-7xl mx-6 ${v === "•" ? "text-brand-green" : "marquee-outline italic"}`}
          >
            {v}
          </span>
        ))}
      </Marquee>
    </div>
  );
}

function About() {
  return (
    <section data-testid="about-section" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <Reveal className="lg:col-span-5 relative">
          <div className="relative rounded-2xl overflow-hidden">
            <img src={IMAGES.office} alt="Avishaa Thakar" className="w-full h-[520px] object-cover" />
          </div>
          <div className="absolute -bottom-6 -right-4 sm:right-6 bg-white border border-brand-border rounded-xl p-6 shadow-sm max-w-[220px]">
            <div className="font-serif text-4xl text-brand-navy">{SITE.experience}+ yrs</div>
            <p className="text-brand-muted text-sm mt-1">of financial planning practice</p>
          </div>
        </Reveal>

        <div className="lg:col-span-7 lg:pl-8">
          <Reveal><Overline>About the founder</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-serif text-4xl lg:text-6xl text-brand-navy font-light leading-tight mt-5">
              Meet {SITE.advisor}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-brand-muted text-lg leading-relaxed mt-6 font-light">
              With over {SITE.experience} years in financial planning and investment advisory, Avishaa founded Arivu Finserve to offer something rare — advice that is genuinely independent and built entirely around <span className="text-brand-navy font-medium">your</span> life.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-brand-muted text-lg leading-relaxed mt-4 font-light">
              Every engagement begins by understanding your goals, your timeline and your comfort with risk. From there, we craft a disciplined plan you can actually follow — and revisit it as life evolves.
            </p>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="grid sm:grid-cols-3 gap-4 mt-10">
              {[
                { icon: Compass, t: "Goal-first" },
                { icon: ShieldCheck, t: "Unbiased" },
                { icon: TrendingUp, t: "Long-term" },
              ].map((f) => (
                <div key={f.t} className="border border-brand-border rounded-xl p-5 bg-white">
                  <f.icon className="h-6 w-6 text-brand-green" />
                  <p className="mt-3 font-medium text-brand-navy">{f.t}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.25}>
            <a
              href={SITE.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-linkedin-btn"
              className="inline-flex items-center gap-2 mt-10 text-brand-navy font-medium border-b-2 border-brand-green pb-1 hover:gap-3 transition-all"
            >
              View LinkedIn profile <ArrowUpRight className="h-4 w-4" />
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function FinancialTeaser() {
  const highlights = ["Cash-flow & budgeting clarity", "Protection & emergency planning", "Tax-efficient goal mapping", "Annual reviews that keep you on track"];
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img src={IMAGES.growth} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-brand-navy/80" />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <Reveal><Overline className="text-brand-sky">Our flagship service</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif text-white text-4xl lg:text-6xl font-light leading-tight mt-5">
                Financial planning, <span className="italic text-brand-accent">reimagined</span> for real life.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-white/70 text-lg font-light mt-6 leading-relaxed max-w-xl">
                Not a product pitch — a living roadmap. We look at your complete financial picture and design a plan that gives every rupee a job and every goal a clear path.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <Link
                to="/services"
                data-testid="teaser-services-btn"
                className="inline-flex items-center gap-2 mt-10 rounded-full bg-brand-accent text-white text-sm font-medium px-8 py-4 hover:bg-white hover:text-brand-navy transition-colors group"
              >
                See all three services <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:pl-10">
            {highlights.map((h, i) => (
              <Reveal key={h} delay={i * 0.08}>
                <div className="flex items-center gap-4 py-5 border-b border-white/15">
                  <span className="flex-shrink-0 h-9 w-9 rounded-full bg-brand-accent/20 flex items-center justify-center">
                    <Check className="h-4 w-4 text-brand-accent" />
                  </span>
                  <span className="text-white/90 text-lg font-light">{h}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you! Avishaa will get back to you shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-brand-bg border border-brand-border rounded-xl px-5 py-4 text-brand-navy placeholder:text-brand-muted/60 focus:outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition";

  return (
    <section data-testid="contact-section" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <Overline>Let&apos;s talk</Overline>
          <h2 className="font-serif text-4xl lg:text-6xl text-brand-navy font-light leading-tight mt-5">
            Ready to plan your future?
          </h2>
          <p className="text-brand-muted text-lg font-light mt-6 leading-relaxed">
            Share a few details and we&apos;ll reach out — or skip ahead and book a free 30-minute consultation directly.
          </p>
          <a
            href={SITE.calendly}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-book-btn"
            className="inline-flex items-center gap-2 mt-8 rounded-full bg-brand-navy text-white text-sm font-medium px-8 py-4 hover:bg-brand-green transition-colors group"
          >
            Book on Calendly <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} data-testid="contact-form" className="bg-white border border-brand-border rounded-2xl p-8 lg:p-10 grid sm:grid-cols-2 gap-5">
            <input data-testid="contact-name" className={field} placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="contact-email" className={field} placeholder="Email address" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input data-testid="contact-phone" className={`${field} sm:col-span-2`} placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <textarea data-testid="contact-message" className={`${field} sm:col-span-2 min-h-[130px] resize-none`} placeholder="Tell us about your goals" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={loading}
              className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-brand-green text-white text-sm font-medium px-8 py-4 hover:bg-brand-navy transition-colors disabled:opacity-60"
            >
              {loading ? "Sending..." : "Send enquiry"} <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function WhyArivu() {
  return (
    <section data-testid="why-section" className="bg-brand-bg border-y border-brand-border">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal><Overline>Why Arivu Finserve</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif text-4xl lg:text-6xl text-brand-navy font-light leading-tight mt-5">
                Where strategy meets <span className="italic text-brand-green">peace of mind.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-brand-muted text-lg font-light mt-6 leading-relaxed">
                We blend human insight with disciplined process — designing strategies around your values and vision, never around a sale.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-brand-border border border-brand-border rounded-2xl overflow-hidden">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.08}>
                <div className="bg-white p-8 lg:p-10 h-full">
                  <span className="font-serif text-2xl text-brand-green">0{i + 1}</span>
                  <h3 className="font-serif text-2xl text-brand-navy mt-4">{w.title}</h3>
                  <p className="text-brand-muted font-light mt-3 leading-relaxed">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section data-testid="testimonials-section" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
      <Reveal>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <div>
            <Overline>Client voices</Overline>
            <h2 className="font-serif text-4xl lg:text-6xl text-brand-navy font-light mt-5 max-w-2xl leading-tight">
              Trusted by families building their future.
            </h2>
          </div>
          <div className="flex items-center gap-2 text-brand-navy">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-5 w-5 fill-brand-green text-brand-green" />
            ))}
            <span className="ml-2 font-light text-brand-muted">5.0 average rating</span>
          </div>
        </div>
      </Reveal>

      <div className="grid md:grid-cols-3 gap-6 mt-14">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.1}>
            <div className="bg-white border border-brand-border rounded-2xl p-8 h-full flex flex-col">
              <Quote className="h-8 w-8 text-brand-green/30" />
              <p className="text-brand-navy/80 font-light leading-relaxed mt-4 flex-grow">{t.quote}</p>
              <div className="flex items-center gap-1 mt-6">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} className="h-4 w-4 fill-brand-green text-brand-green" />
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-brand-border">
                <p className="font-medium text-brand-navy">{t.name}</p>
                <p className="text-brand-muted text-sm">{t.role}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div>
      <Seo
        title="Arivu Finserve | Financial Planning & Investment Advisory, Mumbai"
        description="Independent, goal-based financial planning, retirement and investment advisory by Avishaa Thakar. 8+ years of practice. Book a free 30-minute consultation."
      />
      <Hero />
      <StatBar />
      <EditorialMarquee />
      <About />
      <WhyArivu />
      <FinancialTeaser />
      <Testimonials />
      <ContactSection />
    </div>
  );
}
